/**
 * The Winning squares card under the board: one row per checkpoint with the
 * winner, the square's digits and the score it was set from, then any custom
 * winners an admin added. A checkpoint not reached yet says when it is set
 * ("Set automatically at halftime", "...when the game ends"). Admins get "+ Add a winner"; a Shared
 * grid adds a link to every winner on Results.
 */
import { Button, Card } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  SQUARES_ADD_WINNER,
  SQUARES_GUEST_TAG,
  SQUARES_UNKNOWN_DIGIT,
  SQUARES_WINNING_ADDED_BY_ADMIN,
  SQUARES_WINNING_SET_BY_ADMIN,
  SQUARES_WINNING_TITLE,
  SQUARES_WINNING_UNCLAIMED,
  squaresWinningPendingLine,
  squaresAllWinnersLine,
} from "./squaresCopy";
import type {
  typeSquaresCheckpointRow,
  typeSquaresWinner,
} from "./squaresTypes";

export type typeSquaresWinningCardProps = {
  checkpointRows: readonly typeSquaresCheckpointRow[];
  winners: readonly typeSquaresWinner[];
  topLabel: string;
  sideLabel: string;
  /** "BUF 21 · MIA 17" for one winner's score snapshot. */
  formatScore: (winner: typeSquaresWinner) => string;
  canManage: boolean;
  onAddWinner: () => void;
  /** Shared grids only: how many winners the season has, and the jump. */
  allWinnersCount?: number | null | undefined;
  onOpenResults?: (() => void) | undefined;
};

export const SquaresGuestTag = (): ReactNode => (
  <span className="rounded-full border border-line px-2 text-small text-ink-secondary">
    {SQUARES_GUEST_TAG}
  </span>
);

export function squaresSourceNote(winner: typeSquaresWinner): string | null {
  if (winner.sourceNote === "setByAdmin") return SQUARES_WINNING_SET_BY_ADMIN;
  if (winner.sourceNote === "addedByAdmin") {
    return SQUARES_WINNING_ADDED_BY_ADMIN;
  }
  return null;
}

const WinnerLine = (props: {
  label: string;
  winner: typeSquaresWinner;
  topLabel: string;
  sideLabel: string;
  score: string;
}): ReactNode => {
  const { winner } = props;
  const isUnclaimed =
    winner.ownerId === null ||
    winner.ownerId === undefined ||
    winner.displayName === null ||
    winner.displayName === undefined;
  const note = squaresSourceNote(winner);
  return (
    <li
      className="flex items-start justify-between gap-3 py-2"
      data-testid="squares-winning-row"
      data-winner-id={winner.winnerId}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-caption font-semibold text-ink-secondary">
          {props.label}
        </span>
        <span className="flex flex-wrap items-center gap-2 text-body-strong text-ink">
          {isUnclaimed ? (
            <>
              <span
                aria-hidden="true"
                className="flex h-icon-lg w-icon-lg items-center justify-center rounded-sm border border-line text-caption"
              >
                {SQUARES_UNKNOWN_DIGIT}
              </span>
              {SQUARES_WINNING_UNCLAIMED}
            </>
          ) : (
            <>
              {winner.displayName}
              {winner.isGuest ? <SquaresGuestTag /> : null}
            </>
          )}
        </span>
        <span className="text-caption text-ink-secondary">
          {`${props.topLabel} ${String(winner.topDigit)} · ${props.sideLabel} ${String(winner.sideDigit)}`}
          {note === null ? "" : ` · ${note}`}
        </span>
      </div>
      <span className="shrink-0 text-caption text-ink">{props.score}</span>
    </li>
  );
};

export const SquaresWinningCard = (
  props: typeSquaresWinningCardProps,
): ReactNode => {
  const byId = new Map(
    props.winners.map((winner) => [winner.winnerId, winner]),
  );
  const customWinners = props.winners.filter(
    (winner) => winner.labelKey === "custom",
  );
  return (
    <Card
      title={SQUARES_WINNING_TITLE}
      data-testid="squares-winning-card"
      action={
        props.canManage ? (
          <Button
            variant="ghost"
            size="sm"
            data-testid="squares-add-winner"
            onClick={props.onAddWinner}
          >
            {SQUARES_ADD_WINNER}
          </Button>
        ) : undefined
      }
    >
      <ul className="flex flex-col divide-y divide-hairline">
        {props.checkpointRows.map((row) => {
          const rowWinners = row.winnerIds
            .map((winnerId) => byId.get(winnerId))
            .filter(
              (winner): winner is typeSquaresWinner => winner !== undefined,
            );
          if (row.state !== "set" || rowWinners.length === 0) {
            return (
              <li
                key={row.checkpointKey}
                className="flex items-center justify-between gap-3 py-2"
                data-testid="squares-winning-pending"
              >
                <span className="text-caption font-semibold text-ink-secondary">
                  {row.label}
                </span>
                <span className="text-caption text-ink-secondary">
                  {squaresWinningPendingLine(row)}
                </span>
              </li>
            );
          }
          return rowWinners.map((winner) => (
            <WinnerLine
              key={winner.winnerId}
              label={row.label}
              winner={winner}
              topLabel={props.topLabel}
              sideLabel={props.sideLabel}
              score={props.formatScore(winner)}
            />
          ));
        })}
        {customWinners.map((winner) => (
          <WinnerLine
            key={winner.winnerId}
            label={winner.label}
            winner={winner}
            topLabel={props.topLabel}
            sideLabel={props.sideLabel}
            score={props.formatScore(winner)}
          />
        ))}
      </ul>
      {props.onOpenResults === undefined ||
      props.allWinnersCount === null ||
      props.allWinnersCount === undefined ||
      props.allWinnersCount === 0 ? null : (
        <Button
          variant="link"
          size="sm"
          className="self-start"
          data-testid="squares-all-winners"
          onClick={props.onOpenResults}
        >
          {squaresAllWinnersLine(props.allWinnersCount)}
        </Button>
      )}
    </Card>
  );
};
